import * as React from "react";
import { inject, observer } from "mobx-react";

import { Experiment, isVariantEnabled, isVariantEnabledAndLog } from "components/utility/experiment";

import { WizardHotelPWAv2HotwireView } from "./views/WizardHotelPWAv2HotwireView";
import { WizardHotelPWAv2View } from "./views/WizardHotelPWAv2View";
import { WizardHotelPWAv2HTGView } from "./views/WizardHotelPWAv2HTGView";

import { WizardHotelPWAv2Views } from "./utils/WizardHotelPWAv2Views";
import { WizardHotelPWAv2Props } from "./typings";

const getComponentFromView = (view?: string) => {
  switch (view) {
    case WizardHotelPWAv2Views.HOTWIRE:
      return WizardHotelPWAv2HotwireView;
    case WizardHotelPWAv2Views.HTG:
      return WizardHotelPWAv2HTGView;
    default:
      return WizardHotelPWAv2View;
  }
};

@inject("analytics", "experiment", "page", "wizardState", "compositionStore")
@observer
export class WizardHotelPWAv2 extends React.Component<WizardHotelPWAv2Props> {
  constructor(props: WizardHotelPWAv2Props) {
    super(props);

    const hotelWizardState = this.props.wizardState.hotelWizardState;
    const hotelWizardConfig = hotelWizardState.config;
    const isDesktop = Boolean(
      !this.props.context.deviceInformation?.mobile && !this.props.context.deviceInformation?.tablet
    );
    hotelWizardState.overrideConfig(() => {
      hotelWizardConfig.elementId = this.props.flexComponent.id;
      hotelWizardConfig.heading.fixedTitle = this.props.flexComponent.model.fixedLobTitle === false ? false : true;
      hotelWizardConfig.heading.title = this.props.flexComponent.model.lobTitle;
      hotelWizardConfig.lastSearch.useLastSearchForSingleLOB = this.props.flexComponent.model.useLastSearchForSingleLOB;
      hotelWizardConfig.includeFiltersHotelPWAWizard = this.props.flexComponent.model.includeFiltersHotelPWAWizard;
      hotelWizardConfig.analytics = this.props.analytics;
      hotelWizardConfig.enableMultiplePlaces = this.props.flexComponent.model.enableMultiplePlaces;
      hotelWizardConfig.heading.hideLobTitle = this.props.flexComponent.model.hideLobTitle;
      hotelWizardState.config.heading.lobSubtitleBreakPoint = this.props.flexComponent.model.lobSubtitleBreakPoint;
      hotelWizardConfig.siteIdsWithoutSoftPackages = this.props.flexComponent.model.siteIdsWithoutSoftPackages || [];
      hotelWizardConfig.desktopAutoOpenCalendar = Boolean(
        isDesktop && this.props.flexComponent.model.desktopAutoOpenCalendar
      );

      hotelWizardConfig.enablePackageRates =
        this.props.flexComponent.model.enablePackageRates ?? hotelWizardConfig.enablePackageRates;
      hotelWizardConfig.percentSave = this.props.flexComponent.model.percentSave ?? hotelWizardConfig.percentSave;
      hotelWizardConfig.packageRatesTermsUrl =
        this.props.flexComponent.model.packageRatesTermsUrl ?? hotelWizardConfig.packageRatesTermsUrl;
      hotelWizardConfig.showDestinationField =
        this.props.flexComponent.model.showDestinationField ?? hotelWizardConfig.showDestinationField;

      const currentSiteId = props.context?.site?.id.toString();

      if (
        hotelWizardConfig.siteIdsWithoutSoftPackages.length !== 0 &&
        hotelWizardConfig.siteIdsWithoutSoftPackages.includes(currentSiteId)
      ) {
        hotelWizardConfig.enableAddFlight = false;
        hotelWizardConfig.enableAddCar = false;
      } else {
        hotelWizardConfig.enableAddFlight = this.props.flexComponent.model.enableAddFlight;
        hotelWizardConfig.enableAddCar = this.props.flexComponent.model.enableAddCar;
      }

      let enableEGTA = false;
      if (this.props.context.locale === "en_US") {
        if (this.props.context?.site?.id in ["1", "70201", "70301", "80001"]) {
          enableEGTA = true;
        }
      } else {
        enableEGTA = isVariantEnabled(this.props.context, Experiment.EGTA_BEX_ML_V0);
      }

      hotelWizardState.config.location.destination.essAdapterConfig.egtaExperimentEnabled = enableEGTA;
    });

    hotelWizardState.updateDateFromConfig();

    if (this.props.flexComponent.model.destinationFromContext) {
      hotelWizardState.updateDestinationFromContext(this.props.context);
    }
  }

  /**
   * destinationOnClick
   *
   * Click handler added at this level to avoid duplication in both wizard views (BEX and Hotwire),
   * and to avoid having to determine LOB at form field level (Location.tsx).
   *
   * NOTE: Also runs on focus, so is suitable for keyboard users.
   */
  public destinationOnClick = () => {
    const { context, experiment, page, wizardState } = this.props;

    const isInPrefetchVariant = Boolean(
      experiment &&
        page &&
        isVariantEnabledAndLog(Experiment.LS_Prefetch_JavaScript_Bundles, context, experiment, page, 1)
    );

    wizardState.hotelWizardState.prefetchShoppingPwaAssets(isInPrefetchVariant);
  };

  public componentDidMount() {
    const { hotelWizardState, globalWizardState } = this.props.wizardState;

    const { context, experiment, page, wizardState } = this.props;

    if (globalWizardState.shouldUpdateCookie) {
      hotelWizardState.updateStateFromCookie();
    }

    const isInPrefetchVariant = Boolean(
      experiment &&
        page &&
        isVariantEnabledAndLog(Experiment.LS_Prefetch_JavaScript_Bundles, context, experiment, page, 1)
    );

    wizardState.hotelWizardState.retrieveShoppingPwaManifest(isInPrefetchVariant);
  }

  public render() {
    const ViewComponent = getComponentFromView(this.props.flexComponent.model.view);

    return <ViewComponent {...this.props} destinationOnClick={this.destinationOnClick} />;
  }
}

export default WizardHotelPWAv2;
